import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import { Briefcase, Upload, Eye, X, Pencil, Trash2, FolderPlus, Play, FileText, ImageIcon } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const VISIBILITY_LABELS = { prive: "Privé", employeurs: "Employeurs autorisés", candidature: "Employeur de la candidature" };
const KIND_ICONS = { video: Play, image: ImageIcon, document: FileText };

export default function Portfolio() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], quota: null });
  const [projects, setProjects] = useState([]);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [projectForm, setProjectForm] = useState({ title: "", description: "", metier: "", skills: "", results: "" });
  const [upload, setUpload] = useState({ file: null, title: "", metier: "", skills: "", provenance: "personnelle", project_id: "" });
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
    else if (!authLoading && user && user.role !== "candidat") navigate("/dashboard", { replace: true });
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    const [mine, projs] = await Promise.all([api.get("/showcase/mine"), api.get("/showcase/projects")]);
    setData(mine.data);
    setProjects(projs.data);
  }, []);

  useEffect(() => {
    if (user?.role === "candidat") load().catch((e) => setError(formatApiError(e)));
  }, [user, load]);

  if (!user || user.role !== "candidat") return null;

  const doUpload = async () => {
    if (!upload.file) return;
    setError(""); setNotice(""); setProgress(0);
    const form = new FormData();
    form.append("file", upload.file);
    Object.entries(upload).forEach(([k, v]) => k !== "file" && v && form.append(k, v));
    try {
      await api.post("/showcase/media", form, {
        onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : null),
      });
      setNotice("Média téléversé — traitement en arrière-plan (miniature et vérification).");
      setUpload({ file: null, title: "", metier: "", skills: "", provenance: "personnelle", project_id: "" });
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setProgress(null);
    }
  };

  const saveEdit = async () => {
    setError("");
    try {
      await api.patch(`/showcase/media/${editing.media_id}`, editing);
      setEditing(null);
      await load();
    } catch (err) { setError(formatApiError(err)); }
  };

  const removeMedia = async (mediaId) => {
    setError("");
    try {
      await api.delete(`/showcase/media/${mediaId}`);
      setNotice("Média retiré — son accès est immédiatement invalidé.");
      await load();
    } catch (err) { setError(formatApiError(err)); }
  };

  const createProject = async () => {
    setError("");
    try {
      await api.post("/showcase/projects", {
        ...projectForm,
        skills: projectForm.skills.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setShowProjectForm(false);
      setProjectForm({ title: "", description: "", metier: "", skills: "", results: "" });
      await load();
    } catch (err) { setError(formatApiError(err)); }
  };

  const loadPreview = async () => {
    setError("");
    try {
      const res = await api.get("/showcase/preview");
      setPreview(res.data);
    } catch (err) { setError(formatApiError(err)); }
  };

  const quotaPct = data.quota ? Math.min(100, Math.round((data.quota.used_bytes / data.quota.max_bytes) * 100)) : 0;
  const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} Mo` : `${Math.round(b / 1024)} Ko`);

  return (
    <div data-testid="portfolio-page" className="min-h-screen bg-slate-50">
      <PublicNav />

      <div className="bg-brand-navy">
        <div className="max-w-6xl mx-auto px-6 py-12 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white flex items-center gap-3">
              <Briefcase className="w-7 h-7 text-brand-turquoise" />
              Mon portfolio professionnel
            </h1>
            <p className="text-white/70 text-sm mt-1">
              Vidéos de démonstration, photos de réalisations et certificats. Privé par défaut — vous choisissez ce que les employeurs voient.
            </p>
          </div>
          <button data-testid="preview-button" onClick={loadPreview}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold text-sm rounded-lg px-4 py-2.5 transition-colors">
            <Eye className="w-4 h-4" /> Prévisualiser (vue employeur)
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {error && <div data-testid="portfolio-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
        {notice && <div data-testid="portfolio-notice" className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">{notice}</div>}

        {data.quota && (
          <div data-testid="quota-bar" className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex justify-between text-xs text-slate-500 mb-1.5">
              <span>Quota de stockage</span>
              <span>{fmtSize(data.quota.used_bytes)} / 1 Go · {data.quota.media_count}/{data.quota.max_media} médias</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-brand-turquoise rounded-full transition-[width]" style={{ width: `${quotaPct}%` }} />
            </div>
          </div>
        )}

        <div data-testid="upload-card" className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <h2 className="font-semibold text-brand-navy flex items-center gap-2"><Upload className="w-4 h-4 text-brand-turquoise" /> Ajouter une réalisation</h2>
          <p className="text-xs text-slate-500">Vidéo (mp4, webm, mov — 100 Mo max), photo (png, jpg, webp — 10 Mo) ou certificat (pdf — 10 Mo). Le contenu réel du fichier est vérifié.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input ref={fileRef} data-testid="upload-file" type="file" accept=".mp4,.mov,.webm,.png,.jpg,.jpeg,.webp,.pdf"
              onChange={(e) => setUpload({ ...upload, file: e.target.files[0] || null })}
              className="text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-brand-navy file:text-white file:px-3.5 file:py-2 file:text-xs file:font-semibold" />
            <input data-testid="upload-title" value={upload.title} onChange={(e) => setUpload({ ...upload, title: e.target.value })}
              placeholder="Titre de la réalisation" className="text-sm border border-slate-200 rounded-lg px-3.5 py-2.5" />
            <input data-testid="upload-metier" value={upload.metier} onChange={(e) => setUpload({ ...upload, metier: e.target.value })}
              placeholder="Métier (ex. Soudeur)" className="text-sm border border-slate-200 rounded-lg px-3.5 py-2.5" />
            <input data-testid="upload-skills" value={upload.skills} onChange={(e) => setUpload({ ...upload, skills: e.target.value })}
              placeholder="Compétences démontrées (séparées par des virgules)" className="text-sm border border-slate-200 rounded-lg px-3.5 py-2.5" />
            <select data-testid="upload-provenance" value={upload.provenance} onChange={(e) => setUpload({ ...upload, provenance: e.target.value })}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white">
              <option value="personnelle">Réalisation personnelle</option>
              <option value="collectif">Projet collectif</option>
              <option value="simulation">Simulation</option>
            </select>
            <select data-testid="upload-project" value={upload.project_id} onChange={(e) => setUpload({ ...upload, project_id: e.target.value })}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white">
              <option value="">Sans projet</option>
              {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.title}</option>)}
            </select>
          </div>
          {progress !== null && (
            <div data-testid="upload-progress" className="space-y-1">
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-brand-turquoise rounded-full transition-[width]" style={{ width: `${progress ?? 0}%` }} />
              </div>
              <p className="text-xs text-slate-500">Téléversement… {progress === null ? "" : `${progress} %`}</p>
            </div>
          )}
          <button data-testid="upload-submit" onClick={doUpload} disabled={!upload.file || progress !== null}
            className="bg-brand-turquoise hover:bg-brand-turquoise/90 disabled:opacity-40 text-white text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors">
            Téléverser
          </button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-brand-navy">Projets ({projects.length})</h2>
            <button data-testid="project-new-button" onClick={() => setShowProjectForm(!showProjectForm)}
              className="flex items-center gap-1.5 text-xs font-semibold text-brand-blue hover:text-brand-turquoise">
              <FolderPlus className="w-4 h-4" /> Nouveau projet
            </button>
          </div>
          {showProjectForm && (
            <div data-testid="project-form" className="space-y-2 mb-4 border border-slate-100 rounded-lg p-4">
              <input data-testid="project-title" value={projectForm.title} onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })}
                placeholder="Titre du projet" className="w-full text-sm border border-slate-200 rounded-lg px-3.5 py-2.5" />
              <textarea data-testid="project-description" value={projectForm.description} onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                placeholder="Objectif, tâches effectuées, résultats…" rows={3} className="w-full text-sm border border-slate-200 rounded-lg px-3.5 py-2.5" />
              <button data-testid="project-create-submit" onClick={createProject}
                className="bg-brand-navy text-white text-sm font-semibold rounded-lg px-4 py-2">Créer</button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {projects.map((p) => (
              <div key={p.project_id} data-testid={`project-card-${p.project_id}`} className="border border-slate-100 rounded-lg p-4">
                <p className="font-semibold text-brand-navy text-sm">{p.title}</p>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.description || "Sans description"}</p>
                <p className="text-xs text-brand-turquoise mt-1.5">{p.media_count} média(s)</p>
              </div>
            ))}
          </div>
        </div>

        <div data-testid="media-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.items.length === 0 && (
            <p data-testid="media-empty" className="text-sm text-slate-400 col-span-full text-center py-8">
              Aucune réalisation pour le moment — ajoutez votre première vidéo ou photo de travail.
            </p>
          )}
          {data.items.map((m) => {
            const Icon = KIND_ICONS[m.kind] || FileText;
            return (
              <div key={m.media_id} data-testid={`media-card-${m.media_id}`} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="h-40 bg-slate-100 flex items-center justify-center">
                  {m.kind === "image" && m.has_thumbnail ? (
                    <img src={`${API}/api/showcase/media/${m.media_id}/thumb`} alt={m.title} className="h-full w-full object-cover" />
                  ) : (
                    <Icon className="w-10 h-10 text-slate-300" />
                  )}
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-brand-navy text-sm">{m.title || m.original_filename}</p>
                    <span className="text-[10px] font-bold uppercase bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">{m.kind}</span>
                  </div>
                  <p className="text-xs text-slate-500">{m.metier || "—"} · {fmtSize(m.size)} · {m.view_count} vue(s)</p>
                  <div className="flex items-center justify-between">
                    <span data-testid={`media-visibility-${m.media_id}`} className={`text-[11px] font-medium rounded-full px-2 py-0.5 ${
                      m.visibility === "prive" ? "bg-slate-100 text-slate-500" : "bg-green-50 text-green-700"}`}>
                      {VISIBILITY_LABELS[m.visibility]}
                    </span>
                    <span className="text-[11px] text-slate-400">
                      {m.processing_status === "traite" ? "Prêt" : m.processing_status === "echec" ? "Échec" : "Traitement…"}
                    </span>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button data-testid={`media-edit-${m.media_id}`} onClick={() => setEditing({ ...m, skills: m.skills.join(", ") })}
                      className="flex items-center gap-1 text-xs font-semibold text-brand-blue hover:text-brand-turquoise"><Pencil className="w-3.5 h-3.5" /> Modifier</button>
                    <button data-testid={`media-delete-${m.media_id}`} onClick={() => removeMedia(m.media_id)}
                      className="flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /> Retirer</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editing && (
        <div data-testid="edit-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-brand-navy">Modifier la réalisation</h3>
              <button data-testid="edit-close" onClick={() => setEditing(null)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <input data-testid="edit-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              placeholder="Titre" className="w-full text-sm border border-slate-200 rounded-lg px-3.5 py-2.5" />
            <textarea data-testid="edit-description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              placeholder="Description" rows={3} className="w-full text-sm border border-slate-200 rounded-lg px-3.5 py-2.5" />
            <select data-testid="edit-visibility" value={editing.visibility} onChange={(e) => setEditing({ ...editing, visibility: e.target.value })}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white">
              {Object.entries(VISIBILITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select data-testid="edit-project" value={editing.project_id || ""} onChange={(e) => setEditing({ ...editing, project_id: e.target.value })}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white">
              <option value="">Sans projet</option>
              {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.title}</option>)}
            </select>
            <button data-testid="edit-save" onClick={saveEdit}
              className="w-full bg-brand-turquoise hover:bg-brand-turquoise/90 text-white text-sm font-semibold rounded-lg px-4 py-2.5">Enregistrer</button>
          </div>
        </div>
      )}

      {preview && (
        <div data-testid="preview-modal" className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-10 px-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-brand-navy">Vue employeur</h3>
                <p className="text-xs text-slate-500 mt-1">{preview.note}</p>
              </div>
              <button data-testid="preview-close" onClick={() => setPreview(null)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            {preview.items.length === 0 && (
              <p data-testid="preview-empty" className="text-sm text-slate-400 text-center py-8">
                Aucun média partagé : un employeur ne verrait rien pour le moment. Passez une réalisation en visibilité « Employeurs autorisés ».
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {preview.items.map((m) => (
                <div key={m.media_id} className="border border-slate-100 rounded-lg p-4">
                  <p className="font-semibold text-brand-navy text-sm">{m.title || m.original_filename}</p>
                  <p className="text-xs text-slate-500 mt-1">{m.metier} · {m.skills.join(", ") || "—"}</p>
                  {m.kind === "video" && m.processing_status === "traite" && (
                    <video data-testid={`preview-video-${m.media_id}`} controls preload="metadata"
                      src={`${API}/api/showcase/media/${m.media_id}/content`} className="w-full mt-2 rounded-lg" />
                  )}
                  {m.kind === "image" && m.has_thumbnail && (
                    <img src={`${API}/api/showcase/media/${m.media_id}/thumb`} alt={m.title} className="w-full mt-2 rounded-lg" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
